import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Calendar, Users, Coins } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Results = () => {
  const [draws, setDraws] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDraws();
  }, []);

  const fetchDraws = async () => {
    try {
      const response = await axios.get(`${API}/draws`);
      setDraws(response.data);
    } catch (error) {
      console.error('Error fetching draws:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="results-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">
            Draw Results
          </h1>
          <p className="text-white/60">
            All past draws and winning numbers
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-sol-green border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty State */}
        {!loading && draws.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-6">
              <Trophy className="w-10 h-10 text-white/40" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">No Draws Yet</h3>
            <p className="text-white/60">
              The first draw will happen soon. Buy your tickets now!
            </p>
          </motion.div>
        )}

        {/* Draws List */}
        {!loading && draws.length > 0 && (
          <div className="space-y-6">
            {draws.map((draw, index) => (
              <motion.div
                key={draw.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="card-neon rounded-xl overflow-hidden"
                data-testid={`draw-${draw.id}`}
              >
                {/* Draw Header */}
                <div className="bg-gradient-to-r from-sol-purple/20 to-sol-green/20 p-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <p className="text-white/40 text-sm mb-1">Draw</p>
                      <h3 className="font-heading text-xl font-bold">
                        #{draw.id.slice(0, 8)}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 text-white/60">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {format(new Date(draw.drawn_at), 'MMMM dd, yyyy HH:mm')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Winning Numbers */}
                <div className="p-6">
                  <p className="text-white/40 text-sm mb-4">Winning Numbers</p>
                  <div className="flex flex-wrap gap-3 justify-center mb-6">
                    {draw.winning_numbers.map((num, idx) => (
                      <motion.div
                        key={num}
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.1 + idx * 0.1, type: 'spring' }}
                        className="w-14 h-14 rounded-full flex items-center justify-center font-mono text-xl font-bold text-black"
                        style={{
                          background: 'linear-gradient(135deg, #14F195, #059669)',
                          boxShadow: '0 0 20px rgba(20, 241, 149, 0.5)',
                        }}
                      >
                        {num}
                      </motion.div>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 pt-6 border-t border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-sol-green/10 flex items-center justify-center">
                        <Coins className="w-5 h-5 text-sol-green" />
                      </div>
                      <div>
                        <p className="text-white/40 text-xs">Jackpot</p>
                        <p className="font-mono font-bold">{draw.total_pool.toFixed(2)} USDC</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-sol-purple/10 flex items-center justify-center">
                        <Users className="w-5 h-5 text-sol-purple" />
                      </div>
                      <div>
                        <p className="text-white/40 text-xs">Winners</p>
                        <p className="font-mono font-bold">{draw.winners_count}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Results;
